import React, { FC, useState } from "react";
import { Dialog } from "@mui/material";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { orderDeliverablesTracker, projectDeliverablesTracker } from "../../api/endpoints";
import { DataTable, DataTableColumn } from "../DataTable/DataTable";

/**
 * Deliverables tracker: one row per deliverable file (project × channel ×
 * aspect ratio) with the specs the order promises. The order and project ⋯
 * menus view it (this modal) or download the .xlsx; the same workbook rides in
 * the "All content" zip.
 */

export type TrackerScope = { orderId: number; projectId?: never } | { projectId: number; orderId?: never };

interface TrackerColumn {
  key: string;
  label: string;
}

interface TrackerData {
  title: string;
  columns: TrackerColumn[];
  rows: Record<string, string | number>[];
}

const endpointFor = (scope: TrackerScope) =>
  scope.orderId ? orderDeliverablesTracker(scope.orderId) : projectDeliverablesTracker(scope.projectId as number);

export const downloadDeliverablesTracker = async (scope: TrackerScope) => {
  const toastId = toast.loading("Preparing deliverables tracker...");
  try {
    const { data } = await axiosInstant.get(endpointFor(scope), { params: { format: "xlsx" } });
    const link = document.createElement("a");
    link.href = data.file;
    link.download = data.fileName || "Deliverables Tracker.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("Deliverables tracker downloaded.", { id: toastId });
  } catch (error: any) {
    toast.error(error?.response?.data?.error ?? "Unable to download the deliverables tracker.", { id: toastId });
  }
};

/** The tracker table; open while `scope` is set. */
export const DeliverablesTrackerModal: FC<{ scope: TrackerScope | null; onClose: () => void }> = ({ scope, onClose }) => {
  const open = !!scope;
  const [data, setData] = useState<TrackerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!scope) return;
    setData(null);
    setLoading(true);
    setError(null);
    axiosInstant
      .get(endpointFor(scope))
      .then((response) => setData(response.data))
      .catch((err) => setError(err?.response?.data?.error ?? "Unable to load the deliverables tracker."))
      .finally(() => setLoading(false));
  }, [scope?.orderId, scope?.projectId]);

  const columns: DataTableColumn<Record<string, string | number>>[] = (data?.columns || []).map((c) => ({
    key: c.key,
    header: c.label,
    render: (row) => <span className="whitespace-nowrap">{row[c.key] === "" ? "—" : row[c.key]}</span>,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 1280,
          maxWidth: "calc(100% - 24px)",
          m: { xs: 1.5, sm: 4 },
          borderRadius: "14px",
          fontFamily: "inherit",
          boxShadow: "0 16px 48px rgba(0,0,0,0.1)",
        },
      }}
    >
      <div className="flex flex-col max-h-[calc(100vh-48px)]">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-7 pt-4 sm:pt-6 pb-3 sm:pb-4">
          <div className="min-w-0">
            <h2 className="text-[16px] sm:text-[18px] font-bold text-accent-text">Deliverables tracker</h2>
            {data && (
              <div className="text-[12.5px] text-text-2 mt-1">
                {data.title}: {data.rows.length} {data.rows.length === 1 ? "deliverable" : "deliverables"}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scope && downloadDeliverablesTracker(scope)}
              disabled={loading || !!error}
              className="px-4 py-2 rounded-lg bg-accent text-white text-[13px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-default"
            >
              Download .xlsx
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-surface-2 hover:bg-track text-muted hover:text-text-1 text-[14px] flex items-center justify-center transition-all"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="h-px bg-border-token mx-4 sm:mx-7" />
        <div className="px-4 sm:px-7 py-4 overflow-auto">
          <DataTable
            columns={columns}
            rows={data?.rows || []}
            loading={loading}
            totalCount={data?.rows.length || 0}
            hasMore={false}
            error={error}
            onLoadMore={() => undefined}
            emptyMessage={error || "No deliverables on this yet."}
            getRowKey={(row) => String(row.index)}
          />
        </div>
      </div>
    </Dialog>
  );
};
